import React, { useEffect } from 'react';
import { withFaroProfiler } from '@grafana/faro-react';
import { useOidc } from '@axa-fr/react-oidc';
import { Emitter } from 'rettime';

import { createEmitter, useEmitter } from '../../hooks/useEmitter';

/**
 * Page component split out from App to allow for profiling
 */
const Page = ({
  Component,
  path,
  configureSidebar,
  userDetails,
  oauthAccessToken,
  accessDetails,
  portalJwt,
  startTracking,
  setTheme,
  configureNavigation,
  featureFlags,
  sendError,
  events = { listening: [] },
}: Omit<ModuleComponentProps, 'initAuth' | 'emitter'> & {
  Component: React.ComponentType<ModuleComponentProps>;
  events:
    | {
        listening: (keyof AppEvents)[];
      }
    | undefined;
}): JSX.Element => {
  const { isAuthenticated, login } = useOidc();
  const initAuth = () => {
    if (!isAuthenticated) {
      login();
    }
  };

  const shellEmitter = useEmitter();
  const appEmitter = createEmitter();

  useEffect(() => {
    // Maintain a list of listeners for this app so we can remove them
    const appListeners: [
      keyof AppEvents,
      Emitter.ListenerType<Emitter<AppEvents>, keyof AppEvents>,
    ][] = [];

    if (shellEmitter) {
      // Subscribe to specified events from the shell
      for (let i = 0; i < events.listening.length; i += 1) {
        const eventName = events.listening[i];
        const listener: Emitter.ListenerType<
          typeof shellEmitter,
          typeof eventName
        > = (event) => {
          appEmitter.emit(eventName, event.data);
        };
        shellEmitter.on(eventName, listener);
        appListeners.push([eventName, listener]);
      }
    }

    return () => {
      // Clean up listeners on unmount
      appEmitter.removeAllListeners();
      for (let i = 0; i < appListeners.length; i += 1) {
        const [eventName, listener] = appListeners[i];
        shellEmitter?.removeListener(eventName, listener);
      }
    };
  }, [appEmitter, events.listening, shellEmitter]);

  return (
    <div id="main">
      <Component
        path={path}
        configureSidebar={configureSidebar}
        userDetails={userDetails}
        oauthAccessToken={oauthAccessToken}
        accessDetails={accessDetails}
        portalJwt={portalJwt}
        startTracking={startTracking}
        setTheme={setTheme}
        configureNavigation={configureNavigation}
        featureFlags={featureFlags}
        initAuth={initAuth}
        emitter={appEmitter}
        sendError={sendError}
      />
    </div>
  );
};

export default withFaroProfiler(Page);
